// src/App.tsx
import React, { useState, useEffect } from "react";
import "./App.css";
import Login from "./components/Login";
import Settings from "./components/Settings";
import GameTiles from "./components/GameTiles";
import GameIframe from "./components/GameIframe";
import Balance from "./components/Balance";

const apiUrl = process.env.REACT_APP_API_URL;
const defaultPortal = process.env.REACT_APP_DEFAULT_PORTAL || "PlatformSBet"
const psUrl = apiUrl?.replace("ps-api", "ps") || "";

function App() {
  const [isInIframe, setIsInIframe] = useState(false);
  const [showControls, setShowControls] = useState(false);
  const [portalCode, setPortalCode] = useState(`${defaultPortal}_EUR`);
  const [country, setCountry] = useState("bg");
  const [language, setLanguage] = useState("en");
  const [username, setUsername] = useState("");
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [casinoOperatorId, setCasinoOperatorId] = useState(defaultPortal);
  const [amount, setAmount] = useState(100000);
  const [client, setClient] = useState("DESKTOP");
  const [closeUrl, setCloseUrl] = useState(psUrl);
  const [integrationType, setIntegrationType] = useState("game2slot");
  const [userBalance, setUserBalance] = useState(100000);
  const [bonusBalance, setBonusBalance] = useState(0);
  const [bonusStrategy, setBonusStrategy] = useState("NONE");
  const [iframeSrc, setIframeSrc] = useState("");

  const apiUrl = process.env.REACT_APP_API_URL;
  const apiUrlFree = process.env.REACT_APP_API_URL_FREE;

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data && event.data.type === "G2S_reload") {
        window.location.reload();
      }
    };

    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, []);

  // --- Login handlers ---
  const handleLogin = (username: string) => {
    setIsLoggedIn(true);
    setUsername(username);
  };

  const handleLogout = () => {
    setIsLoggedIn(false);
    setUsername("");
  };

  // --- Play handlers ---
  const handlePlayWithRealMoneyClick = (gameId: number) => {
    if (!isLoggedIn) {
      alert("You have to be logged in to play with real money!");
      return;
    }

    const playerId = username;
    const width = 1024;
    const height = 570;
    const windowFeatures = `width=${width},height=${height},menubar=no,toolbar=no,location=no,status=no,resizable=yes,scrollbars=yes`;
    const gameCloseURL = isInIframe && showControls ? "" : closeUrl;
    const urlParams = new URLSearchParams({
      playerId: playerId,
      portalCode: portalCode,
      country: country,
      language: language,
      gameId: String(gameId),
      client: client,
      closeUrl: encodeURIComponent(gameCloseURL),
      integrationType: integrationType,
    });

    const url = `${apiUrl}/api/game/generate/url?${urlParams.toString()}`;

    fetch(url)
      .then((response) => {
        if (!response.ok) {
          return response.text().then((errorText) => {
            throw new Error(`HTTP ${response.status}: ${errorText}`);
          });
        }
        return response.text();
      })
      .then((data) => {
        if (isInIframe) {
          setIframeSrc(data);
          return;
        }
        window.open(data, "_blank", windowFeatures);
      })
      .catch((error) => {
        if (isInIframe) {
          setIframeSrc(
            `data:text/html;charset=utf-8,${encodeURIComponent(error.message)}`
          );
          return;
        }
        alert(`Error launching game: ${error.message}`);
      });
  };

  const handlePlayForFreeClick = (gameId: number) => {
    const width = 1024;
    const height = 570;
    const windowFeatures = `width=${width},height=${height},menubar=no,toolbar=no,location=no,status=no,resizable=yes,scrollbars=yes,noopener,noreferrer,top=100,left=100`;
    const gameCloseURL = isInIframe && showControls ? "" : closeUrl;
    const urlParams = new URLSearchParams({
      casinoOperatorId: casinoOperatorId,
      gameId: String(gameId),
      portalCode: `${casinoOperatorId}_G2S`,
      amount: String(amount),
      language: language,
      client: client,
      closeUrl: encodeURIComponent(gameCloseURL),
      integrationType: integrationType,
    });

    const url = `${apiUrlFree}/api/free/game/generate/url?${urlParams.toString()}`;

    fetch(url)
      .then((response) => {
        if (!response.ok) {
          return response.text().then((errorText) => {
            throw new Error(`HTTP ${response.status}: ${errorText}`);
          });
        }
        return response.text();
      })
      .then((data) => {
        if (isInIframe) {
          setIframeSrc(data);
          return;
        }
        window.open(data, "_blank", windowFeatures);
      })
      .catch((error) => {
        console.error("Error:", error);
        if (isInIframe) {
          setIframeSrc(
            `data:text/html;charset=utf-8,${encodeURIComponent(error.message)}`
          );
          return;
        }
        alert(`Error launching game: ${error.message}`);
      });
  };

  return (
    <div className="App">
      <header className="App-header">
        <h1 className="app-title">Platform Simulator</h1>

        <Login
          onLogin={handleLogin}
          onLogout={handleLogout}
          isLoggedIn={isLoggedIn}
        />

        {isLoggedIn && <Balance casinoUserId={username} />}

        <Settings
          isLoggedIn={isLoggedIn}
          isInIframe={isInIframe}
          setIsInIframe={setIsInIframe}
          showControls={showControls}
          setShowControls={setShowControls}
          portalCode={portalCode}
          setPortalCode={setPortalCode}
          country={country}
          setCountry={setCountry}
          language={language}
          setLanguage={setLanguage}
          casinoOperatorId={casinoOperatorId}
          setCasinoOperatorId={setCasinoOperatorId}
          amount={amount}
          setAmount={setAmount}
          client={client}
          setClient={setClient}
          closeUrl={closeUrl}
          setCloseUrl={setCloseUrl}
          integrationType={integrationType}
          setIntegrationType={setIntegrationType}
          userBalance={userBalance}
          setUserBalance={setUserBalance}
          bonusBalance={bonusBalance}
          setBonusBalance={setBonusBalance}
          bonusStrategy={bonusStrategy}
          setBonusStrategy={setBonusStrategy}
          username={username}
        />

        <GameTiles
          handlePlayForFree={handlePlayForFreeClick}
          handlePlayForReal={handlePlayWithRealMoneyClick}
        />
        <GameIframe
          iframeSrc={iframeSrc}
          showControls={showControls}
          onClose={() => setIframeSrc("")}
        />
      </header>
    </div>
  );
}

export default App;
